import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useState, useEffect } from "react";
import { getLogoDesigns, getFlyerDesigns, type DesignAsset } from "@/services/localAssets";
import { Link } from "react-router-dom";

export const DesignSection = () => {
  const [logoDesigns, setLogoDesigns] = useState<DesignAsset[]>([]);
  const [flyerDesigns, setFlyerDesigns] = useState<DesignAsset[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    try {
      setIsLoading(true);
      
      // Fetch designs from local assets
      const logos = getLogoDesigns();
      const flyers = getFlyerDesigns();
      
      console.log("Fetched logo designs:", logos);
      console.log("Fetched flyer designs:", flyers);
      
      setLogoDesigns(logos);
      setFlyerDesigns(flyers);
    } catch (err) {
      console.error("Error fetching designs:", err);
      setError("Failed to load designs.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Image error handler
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.warn("Image failed to load:", e.currentTarget.src);
    e.currentTarget.src = "https://via.placeholder.com/300x300?text=Image+Not+Found";
  };

  return (
    <section className="py-20 px-4 bg-accent-navy">
      <div className="max-w-7xl mx-auto">
        {error && (
          <div className="text-red-500 bg-red-100 p-4 rounded-md mb-6">
            {error}
          </div>
        )}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
        >
          {/* Logo Design Section */}
          <Card className="bg-accent-navy border-none">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-white">Logo Design</h3>
                <Link 
                  to="/logo-designs" 
                  className="text-white/80 hover:text-white text-sm underline transition-colors"
                >
                  View All
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {isLoading
                  ? Array(2)
                      .fill(0)
                      .map((_, index) => (
                        <div key={index} className="aspect-square rounded-lg overflow-hidden">
                          <Skeleton className="h-full w-full" />
                        </div>
                      ))
                  : logoDesigns.slice(0, 4).map((design) => (
                      <Link to="/logo-designs" key={design.id}>
                        <motion.div
                          whileHover={{ scale: 1.05 }}
                          className="relative aspect-square rounded-lg overflow-hidden cursor-pointer"
                        >
                          <img
                            src={design.url}
                            alt={design.title}
                            className="object-cover w-full h-full"
                            onError={handleImageError}
                          />
                          <div className="absolute inset-0 bg-black/30 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                            <span className="text-white font-semibold">View More</span>
                          </div>
                        </motion.div>
                      </Link>
                    ))
                }
              </div>
            </CardContent>
          </Card>

          {/* Flyer Design Section */}
          <Card className="bg-accent-navy border-none">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-white">Flyer Design</h3>
                <Link 
                  to="/flyer-designs" 
                  className="text-white/80 hover:text-white text-sm underline transition-colors text-coral"
                >
                  View All
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {isLoading
                  ? Array(2)
                      .fill(0)
                      .map((_, index) => (
                        <div key={index} className="aspect-square rounded-lg overflow-hidden">
                          <Skeleton className="h-full w-full" />
                        </div>
                      ))
                  : flyerDesigns.slice(0, 4).map((design) => (
                      <Link to="/flyer-designs" key={design.id}>
                        <motion.div
                          whileHover={{ scale: 1.05 }}
                          className="relative aspect-square rounded-lg overflow-hidden cursor-pointer"
                        >
                          <img
                            src={design.url}
                            alt={design.title}
                            className="object-cover w-full h-full"
                            onError={handleImageError}
                          />
                          <div className="absolute inset-0 bg-black/30 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                            <span className="text-white font-semibold">View More</span>
                          </div>
                        </motion.div>
                      </Link>
                    ))
                }
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};